/**
 * Workspace Membership
 * 
 * Mirror collection stored in users/{userId}/workspace_memberships/{workspaceId}
 * Allows quick lookup of all workspaces a user belongs to
 * 
 * @version 2.0.0
 */

import { Timestamp } from '@angular/fire/firestore';
import { WorkspaceType, WorkspaceRole } from './workspace';

/**
 * Workspace Membership
 * Mirror document representing a user's membership in a workspace
 * Stored at: users/{userId}/workspace_memberships/{workspaceId}
 */
export interface WorkspaceMembership {
  workspaceId: string;
  workspaceName: string;
  workspaceType: WorkspaceType;
  workspaceColor?: string;
  workspaceIcon?: string;
  role: WorkspaceRole;
  ownerId: string;
  ownerEmail: string;
  joinedAt: Date | Timestamp;
  lastAccessedAt: Date | Timestamp;
}

/**
 * User Workspace Preferences
 * Stored at: users/{userId}/preferences/workspace
 */
export interface UserWorkspacePreferences {
  defaultWorkspaceId: string;      // Workspace to load on login
  lastActiveWorkspaceId?: string;  // Last workspace user was in
  workspaceOrder?: string[];       // Custom ordering for workspace list
  favoriteWorkspaces?: string[];   // Pinned/favorite workspaces
}

/**
 * Workspace Membership Class
 * Provides helper methods and type conversion
 */
export class WorkspaceMembershipModel implements WorkspaceMembership {
  workspaceId: string;
  workspaceName: string;
  workspaceType: WorkspaceType;
  workspaceColor?: string;
  workspaceIcon?: string;
  role: WorkspaceRole;
  ownerId: string;
  ownerEmail: string;
  joinedAt: Date;
  lastAccessedAt: Date;

  constructor(data: Partial<WorkspaceMembership> = {}) {
    this.workspaceId = data.workspaceId || '';
    this.workspaceName = data.workspaceName || '';
    this.workspaceType = data.workspaceType || 'personal';
    this.workspaceColor = data.workspaceColor;
    this.workspaceIcon = data.workspaceIcon;
    this.role = data.role || 'viewer';
    this.ownerId = data.ownerId || '';
    this.ownerEmail = data.ownerEmail || '';
    
    // Handle Firestore Timestamps
    if (data.joinedAt) {
      this.joinedAt = data.joinedAt instanceof Timestamp 
        ? data.joinedAt.toDate() 
        : data.joinedAt as Date;
    } else {
      this.joinedAt = new Date();
    }
    
    if (data.lastAccessedAt) {
      this.lastAccessedAt = data.lastAccessedAt instanceof Timestamp 
        ? data.lastAccessedAt.toDate() 
        : data.lastAccessedAt as Date;
    } else {
      this.lastAccessedAt = new Date();
    }
  }

  /**
   * Check if user is owner of workspace
   */
  isOwner(userId: string): boolean {
    return this.ownerId === userId;
  }

  /**
   * Check if user is admin or owner
   */
  isAdminOrOwner(): boolean {
    return this.role === 'owner' || this.role === 'admin';
  }

  /**
   * Check if user can edit in this workspace
   */
  canEdit(): boolean {
    return ['owner', 'admin', 'standard'].includes(this.role);
  }

  /**
   * Check if user can delete in this workspace
   */
  canDelete(): boolean {
    return ['owner', 'admin', 'standard'].includes(this.role);
  }

  /**
   * Check if user can manage members
   */
  canManageMembers(): boolean {
    return this.role === 'owner' || this.role === 'admin';
  }

  /**
   * Check if user is viewer only
   */
  isViewerOnly(): boolean {
    return this.role === 'viewer';
  }

  /**
   * Check if workspace is personal
   */
  isPersonal(): boolean {
    return this.workspaceType === 'personal';
  }

  /**
   * Check if workspace is professional/shared
   */
  isProfessional(): boolean {
    return ['professional', 'household', 'business', 'agency'].includes(this.workspaceType);
  }

  /**
   * Get workspace icon
   */
  getIcon(): string {
    if (this.workspaceIcon) return this.workspaceIcon;
    
    switch (this.workspaceType) {
      case 'personal':
        return '👤';
      case 'professional':
        return '🏢';
      case 'household':
        return '🏠';
      case 'business':
        return '🔧';
      case 'agency':
        return '🏘️';
      default:
        return '📁';
    }
  }

  /**
   * Get workspace color
   */
  getColor(): string {
    if (this.workspaceColor) return this.workspaceColor;
    
    switch (this.workspaceType) {
      case 'personal':
        return '#6366F1';
      case 'professional':
        return '#3B82F6';
      case 'household':
        return '#10B981';
      case 'business':
        return '#F59E0B';
      case 'agency':
        return '#8B5CF6';
      default:
        return '#6B7280';
    }
  }

  /**
   * Get role display name
   */
  getRoleDisplayName(): string {
    switch (this.role) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'standard':
        return 'Member';
      case 'viewer':
        return 'Viewer';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get workspace type display name
   */
  getTypeDisplayName(): string {
    switch (this.workspaceType) {
      case 'personal':
        return 'Personal';
      case 'professional':
        return 'Professional';
      case 'household':
        return 'Household';
      case 'business':
        return 'Business';
      case 'agency':
        return 'Agency';
      default:
        return 'Unknown';
    }
  }

  /**
   * Convert to plain object for Firestore
   */
  toFirestore(): Record<string, unknown> {
    return {
      workspaceId: this.workspaceId,
      workspaceName: this.workspaceName,
      workspaceType: this.workspaceType,
      workspaceColor: this.workspaceColor,
      workspaceIcon: this.workspaceIcon,
      role: this.role,
      ownerId: this.ownerId,
      ownerEmail: this.ownerEmail,
      joinedAt: this.joinedAt,
      lastAccessedAt: this.lastAccessedAt
    };
  }
}

